// Print math and an independent SVG compatibility capture. Live plan DOM,
// camera, selection and navigation caches are not output resources.
import { capturePlanSvg } from '../render/plan_svg_capture.js';
import { fitCameraToAllPlanContent } from '../viewport/viewport_controller.js?v=20260706d';
import {
  applyPrintPlanSvgGroupFilter,
  selectedPrintPlanGroupBounds
} from './print_plan_group_filter.js?v=20260904c';

const PRINT_SCALE_PRESETS = Object.freeze([1, 2, 3, 4, 5, 10, 15, 20, 25, 50, 100, 200, 500, 1000]);
// Outer bounds for the zoom strip — wider than the presets so all ticks sit
// with visual headroom instead of crowding the strip's ends. Deliberately
// independent of the live app's PLAN_MIN_SCALE/PLAN_MAX_SCALE (0.01/10):
// those clamp S.camera2D and are unrelated to this modal's own camera.
const PRINT_SCALE_N_MIN = 0.5;
const PRINT_SCALE_N_MAX = 2000;

const PAPER_SIZES_MM = Object.freeze({
  A4: Object.freeze({ width: 297, height: 210 }),
  A3: Object.freeze({ width: 420, height: 297 })
});

// Used until the real admin-configured settings (per-format margins/border,
// fetched from /api/print/paper-formats) arrive, and as a safety fallback
// for any format code the backend doesn't (yet) return a row for.
const DEFAULT_PAPER_FORMAT_SETTINGS = Object.freeze({
  margin_top_mm: 10,
  margin_right_mm: 10,
  margin_bottom_mm: 10,
  margin_left_mm: 10,
  border_thickness_mm: 0.35,
  titleblock_border_thickness_mm: 0.3
});

// Title-block (Zeichnungskopf) geometry: a fixed physical size regardless of
// paper format/scale, anchored to the print frame's bottom-right corner, in
// 4 fixed-width horizontal sections (mm) — see print_plan_modal.js for the
// actual HTML/SVG markup built from these numbers. The "logo" section is the
// per-Zeichnungskopf company logo (admin-selectable); the small fixed KS
// Studio brand mark lives inside "meta", below Druckdatum/Massstab/
// Blattformat, unrelated to that company logo.
const TITLE_BLOCK_WIDTH_MM = 180;
const TITLE_BLOCK_HEIGHT_MM = 27;
const TITLE_BLOCK_OVERLAP_MM = 0.15;
// One uniform text size across every field (the 6 address/contact lines in
// the logo section, both label and value in Betrifft/Produktinformation,
// and the meta date/scale/format lines) — deliberately not differentiated
// per section, per explicit spec.
const TITLE_BLOCK_FONT_SIZE_MM = 2.5;
const TITLE_BLOCK_SECTION_WIDTHS_MM = Object.freeze({
  logo: 60,
  betrifft: 54,
  produktinformation: 40,
  meta: TITLE_BLOCK_WIDTH_MM - 60 - 54 - 40
});

// Neutral fallback logo when a template has no logo configured for the
// active language (and none on its base/default record either) — the same
// asset already used as the BOM print header's default logo.
const DEFAULT_TITLE_BLOCK_LOGO = 'assets/img/Logos/ks_studio_logo.svg';

function normalizePaperFormat(value) {
  return value === 'A3' ? 'A3' : 'A4';
}

function frameSizeMm(paperFormat, marginSettings = DEFAULT_PAPER_FORMAT_SETTINGS) {
  const paper = PAPER_SIZES_MM[normalizePaperFormat(paperFormat)];
  const m = marginSettings || DEFAULT_PAPER_FORMAT_SETTINGS;
  return {
    width: paper.width - (Number(m.margin_left_mm) || 0) - (Number(m.margin_right_mm) || 0),
    height: paper.height - (Number(m.margin_top_mm) || 0) - (Number(m.margin_bottom_mm) || 0)
  };
}

// The on-screen frame size is NOT a fixed physical CSS-mm value — it's
// fit dynamically so the paper frame always occupies `fill` (95%) of the
// viewer, on whichever axis is the binding constraint. This guarantees the
// frame is always fully visible and looks identical for A4/A3 (both ISO-216
// landscape, same aspect ratio), fixing both the "A3 doesn't fit" bug and
// the "A4 looks tiny" complaint. Returns CSS-px-per-PAPER-mm.
function frameFitScale(paperFormat, viewerSize, marginSettings = DEFAULT_PAPER_FORMAT_SETTINGS, fill = 0.95) {
  const mm = frameSizeMm(paperFormat, marginSettings);
  const w = Math.max(1, Number(viewerSize?.w) || 1);
  const h = Math.max(1, Number(viewerSize?.h) || 1);
  return Math.min((w * fill) / mm.width, (h * fill) / mm.height);
}

// previewCamera state stores the real print ratio `n` directly (not a
// screen scale) — this is the key simplification that makes A4<->A3
// toggling and window-resizing "just work": since `n` is the source of
// truth, the real-world print scale never silently drifts when the on-
// screen fit-scale changes; only the visual representation adapts.
// screenScaleForN converts to CSS-px-per-world-mm (same convention as
// S.camera2D.scale) for use in the screen-space transform math below.
function screenScaleForN(n, fitScale) {
  return fitScale / Math.max(0.0001, Number(n) || 1);
}

function clampN(n) {
  return Math.min(PRINT_SCALE_N_MAX, Math.max(PRINT_SCALE_N_MIN, Number(n) || PRINT_SCALE_N_MIN));
}

// Log-scale position along the zoom strip, 0..1, for a given "1:N" ratio.
// Used both to place the fixed preset ticks and to reposition the live
// zoom indicator so they can never drift apart from previewCamera.n.
function zoomStripFraction(n) {
  const safeN = Math.max(PRINT_SCALE_N_MIN, Math.min(PRINT_SCALE_N_MAX, Number(n) || PRINT_SCALE_N_MIN));
  const logMin = Math.log10(PRINT_SCALE_N_MIN);
  const logMax = Math.log10(PRINT_SCALE_N_MAX);
  return Math.min(1, Math.max(0, (Math.log10(safeN) - logMin) / (logMax - logMin)));
}

// Inverse of zoomStripFraction — turns a 0..1 strip position back into an
// "1:N" ratio, used when the user drags the indicator handle directly.
function nForZoomStripFraction(fraction) {
  const logMin = Math.log10(PRINT_SCALE_N_MIN);
  const logMax = Math.log10(PRINT_SCALE_N_MAX);
  const clamped = Math.min(1, Math.max(0, Number(fraction) || 0));
  return Math.pow(10, logMin + clamped * (logMax - logMin));
}

// Kept as a compatible API name; the live plan is no longer rendered or cloned.
function captureLivePlanSnapshot(options = {}) {
  const explicit = Object.hasOwn(options, 'selectedGroupKeys');
  return capturePlanSvg({
    ...(explicit ? { selectedGroupKeys: options.selectedGroupKeys } : {}),
    ...(options.size ? { size: options.size } : {}),
    prepare() {
      const bounds = explicit ? selectedPrintPlanGroupBounds({ selectedGroupKeys: options.selectedGroupKeys }) : null;
      if (bounds) fitCameraToAllPlanContent({ bounds });
      else if (!explicit || options.selectedGroupKeys == null) fitCameraToAllPlanContent();
    },
    filter: svg => applyPrintPlanSvgGroupFilter(svg, { selectedGroupKeys: explicit ? options.selectedGroupKeys : undefined })
  });
}

// The independent snapshot's markup already has SCREEN coordinates baked in
// (worldToScreen was applied at capture time using snapshotCamera/
// snapshotSize). To pan/zoom it live in the modal without ever calling
// render2D() again, compose a single affine that (1) undoes that baking
// back to world-mm, then (2) reapplies it against the modal's OWN
// independent previewCamera/viewerSize. Both steps are uniform-scale +
// translate, so they combine into one matrix(a,0,0,a,e,f).
function composePreviewTransform({ snapshotCamera, snapshotSize, previewCamera, viewerSize }) {
  const a = previewCamera.scale / snapshotCamera.scale;
  const e = viewerSize.w / 2 - (snapshotSize.w / 2) * a + (snapshotCamera.x - previewCamera.x) * previewCamera.scale;
  const f = viewerSize.h / 2 - (snapshotSize.h / 2) * a + (snapshotCamera.z - previewCamera.z) * previewCamera.scale;
  return { a, e, f };
}

// World-mm coordinate sitting at the (fixed, screen-centered) print frame's
// top-left corner, given the current previewCamera and the frame's on-
// screen CSS-px size. Used both to draw the frame and, at print time, as
// the origin for the physically-accurate 1:N print transform.
function frameWorldOrigin({ previewCamera, frameSizePx }) {
  return {
    x: previewCamera.x - (frameSizePx.w / 2) / previewCamera.scale,
    z: previewCamera.z - (frameSizePx.h / 2) / previewCamera.scale
  };
}

// Print-time transform: maps the snapshot's baked screen coordinates
// directly into the print SVG's viewBox, which is set up in real paper
// millimetres (see print_plan_modal.js#buildPrintSvgMarkup). This is what
// makes the output genuinely 1:N on paper (unlike the BOM plan print's
// fit-to-page stretch) — SVG content sized via physical CSS units (mm)
// renders at true physical size regardless of printer DPI.
function buildPrintSvgTransform({ snapshotCamera, snapshotSize, origin, n }) {
  const a = 1 / (snapshotCamera.scale * n);
  const e = (snapshotCamera.x - origin.x) / n - (snapshotSize.w / 2) * a;
  const f = (snapshotCamera.z - origin.z) / n - (snapshotSize.h / 2) * a;
  return { a, e, f };
}

export {
  PRINT_SCALE_PRESETS,
  PRINT_SCALE_N_MIN,
  PRINT_SCALE_N_MAX,
  PAPER_SIZES_MM,
  DEFAULT_PAPER_FORMAT_SETTINGS,
  TITLE_BLOCK_WIDTH_MM,
  TITLE_BLOCK_HEIGHT_MM,
  TITLE_BLOCK_OVERLAP_MM,
  TITLE_BLOCK_FONT_SIZE_MM,
  TITLE_BLOCK_SECTION_WIDTHS_MM,
  DEFAULT_TITLE_BLOCK_LOGO,
  normalizePaperFormat,
  frameSizeMm,
  frameFitScale,
  screenScaleForN,
  clampN,
  zoomStripFraction,
  nForZoomStripFraction,
  captureLivePlanSnapshot,
  composePreviewTransform,
  frameWorldOrigin,
  buildPrintSvgTransform
};
